import { apiSlice } from '../../../api/common/apiSlice';
import { ApiTagType } from '../../../constants/apiTagType';
import {
  ApproveCompanyWithdrawRequestResponse,
  CreateCompanyWithdrawRequestRequest,
  CreateCompanyWithdrawRequestResponse,
  FetchCompanyWithdrawRequestsResponse,
  FetchOnboardingEmployeesResponse,
  RejectCompanyWithdrawRequestResponse,
} from '../../../types/b2tRatibiPlus';
import {
  AcceptTreasuryAgreementsResponse,
  ApproveCompanyForTreasuryRequest,
  ApproveCompanyForTreasuryResponse,
  ApproveTreasuryEmployeeResponse,
  ConfirmTreasuryKycResponse,
  CreateTreasuryContributionResponse,
  CreateTreasuryEmployeeRequest,
  CreateTreasuryEmployeeResponse,
  CreateTreasuryRedemptionRequest,
  CreateTreasuryRedemptionResponse,
  DeleteTreasuryRedeemAutomationResponse,
  ExecuteTreasuryContributionResponse,
  FetchTreasuryAlpacaOnboardingResponse,
  FetchTreasuryAnalysisResponse,
  FetchTreasuryContributionsResponse,
  FetchTreasuryKybReadinessResponse,
  FetchTreasuryKybResponse,
  FetchTreasuryKycConfirmationResponse,
  FetchTreasuryOwnerAgreementResponse,
  FetchTreasuryRedeemAutomationRunsResponse,
  FetchTreasuryRedeemAutomationsResponse,
  FetchTreasurySetupResponse,
  RetryTreasuryAlpacaOnboardingResponse,
  RetryTreasuryWorldCheckResponse,
  SaveTreasuryKybRequest,
  SaveTreasuryKybResponse,
  SaveTreasuryRedeemAutomationRequest,
  SaveTreasuryRedeemAutomationResponse,
  SignTreasuryOwnerAgreementResponse,
  TreasuryListQuery,
  UpdateTreasuryPersonalEmailRequest,
  UpdateTreasuryPersonalEmailResponse,
  UpdateTreasuryRedeemAutomationRequest,
  UpdateTreasuryReinvestDividendResponse,
  VerifyTreasuryKybRequest,
  VerifyTreasuryKybResponse,
} from '../../../types/treasury';
import { toQueryParams } from '../../../utils/request';

type CompanyArg = { companyId: number };
type EmployeeArg = CompanyArg & { employeeId: number };
type RuleArg = CompanyArg & { id: number };
type ListArg = CompanyArg & TreasuryListQuery;

const ADMIN = '/admin';
const TREASURY = `${ADMIN}/treasury`;

/**
 * Treasury reads are money and account state: never served from a cache another screen left behind. A screen that
 * mounts reads again (the old axios calls did the same), and a cached answer dies with its last screen.
 */
const FRESH = { keepUnusedDataFor: 0 };

/**
 * The company treasury — onboarding (setup, owner, KYC, KYB, agreements, Alpaca account) and the day-to-day money
 * (contributions, redemptions, redemption rules, withdrawals). All through madkhol-admin → thriftplan. Tag ids are the
 * company id, so one company's write never refetches another's screen.
 */
export const treasuryApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ---------------------- ONBOARDING ----------------------
    /** Rejects with 404 / TREASURY_NOT_SET_UP when the company has none. */
    fetchTreasurySetup: builder.query<FetchTreasurySetupResponse['data'], number>({
      query: (companyId) => `${TREASURY}/thrift-companies/company/${companyId}`,
      providesTags: (res, error, companyId) => [{ type: ApiTagType.TreasurySetup, id: companyId }],
      ...FRESH,
    }),
    /** Step 1 — creates the treasury bank, group and department, and the PENDING setup. */
    approveCompanyForTreasury: builder.mutation<
      ApproveCompanyForTreasuryResponse['data'],
      CompanyArg & { payload: ApproveCompanyForTreasuryRequest }
    >({
      query: ({ companyId, payload }) => ({
        url: `${ADMIN}/thrift-companies/${companyId}/approve-treasury`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (res, error, { companyId }) => [{ type: ApiTagType.TreasurySetup, id: companyId }],
    }),
    /** The treasury employee's onboarding rows, newest first. */
    fetchTreasuryOnboarding: builder.query<FetchOnboardingEmployeesResponse['data'], number>({
      query: (companyId) => `${TREASURY}/thrift-company-group-employees/company/${companyId}/onboarding`,
      providesTags: (res, error, companyId) => [{ type: ApiTagType.TreasuryOnboarding, id: companyId }],
      ...FRESH,
    }),
    /** Step 2 — queues the treasury employee's onboarding; the onboarding row's status shows when it is done. */
    createTreasuryEmployee: builder.mutation<
      CreateTreasuryEmployeeResponse['data'],
      CompanyArg & { payload: CreateTreasuryEmployeeRequest }
    >({
      query: ({ companyId, payload }) => ({
        url: `${TREASURY}/thrift-company-group-employees/company/${companyId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (res, error, { companyId }) => [{ type: ApiTagType.TreasuryOnboarding, id: companyId }],
    }),
    /**
     * Step 3 — the treasury employee's personal email. Leaves no mark the setup shows (it only replaces a still-work
     * email), so nothing is refetched.
     */
    updateTreasuryPersonalEmail: builder.mutation<
      UpdateTreasuryPersonalEmailResponse['data'],
      EmployeeArg & { payload: UpdateTreasuryPersonalEmailRequest }
    >({
      query: ({ companyId, employeeId, payload }) => ({
        url: `${TREASURY}/user-employee/company/${companyId}/employee/${employeeId}/personal-email`,
        method: 'PUT',
        body: payload,
      }),
    }),
    /** Step 4 — approves the treasury employee; the setup becomes ACTIVE. */
    approveTreasuryEmployee: builder.mutation<ApproveTreasuryEmployeeResponse['data'], EmployeeArg>({
      query: ({ companyId, employeeId }) => ({
        url: `${TREASURY}/user-employee/company/${companyId}/employee/${employeeId}/approve`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (res, error, { companyId }) => [
        { type: ApiTagType.TreasurySetup, id: companyId },
        { type: ApiTagType.TreasuryKycConfirmation, id: companyId },
      ],
    }),
    /** The owner's KYC confirmation date, or confirmDate null while it isn't confirmed. */
    fetchTreasuryKycConfirmation: builder.query<FetchTreasuryKycConfirmationResponse['data'], EmployeeArg>({
      query: ({ companyId, employeeId }) =>
        `${TREASURY}/user-employee/company/${companyId}/employee/${employeeId}/kyc-confirmation`,
      providesTags: (res, error, { companyId }) => [{ type: ApiTagType.TreasuryKycConfirmation, id: companyId }],
      ...FRESH,
    }),
    /** Confirms the owner's KYC — sets the confirm date when empty; a confirmed KYC keeps its date. */
    confirmTreasuryKyc: builder.mutation<ConfirmTreasuryKycResponse['data'], EmployeeArg>({
      query: ({ companyId, employeeId }) => ({
        url: `${TREASURY}/user-employee/company/${companyId}/employee/${employeeId}/kyc-confirmation`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (res, error, { companyId }) => [{ type: ApiTagType.TreasuryKycConfirmation, id: companyId }],
    }),
    /** The owner's account opening agreement signature (what the mobile app's agreement screen records), or null. */
    fetchTreasuryOwnerAgreement: builder.query<FetchTreasuryOwnerAgreementResponse['data'], EmployeeArg>({
      query: ({ companyId, employeeId }) =>
        `${TREASURY}/user-employee/company/${companyId}/employee/${employeeId}/account-opening-agreement`,
      providesTags: (res, error, { companyId }) => [{ type: ApiTagType.TreasuryOwnerAgreement, id: companyId }],
      ...FRESH,
    }),
    /**
     * Signs the owner's account opening agreement for them (company contract) — the same madkhol-user call as the
     * mobile app. madkhol-user then moves the owner's status: Active (opens their personal Alpaca account), Pending,
     * or Manual Check Required — which the readiness lists.
     */
    signTreasuryOwnerAgreement: builder.mutation<SignTreasuryOwnerAgreementResponse['data'], EmployeeArg>({
      query: ({ companyId, employeeId }) => ({
        url: `${TREASURY}/user-employee/company/${companyId}/employee/${employeeId}/account-opening-agreement`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (res, error, { companyId }) => [
        { type: ApiTagType.TreasuryOwnerAgreement, id: companyId },
        { type: ApiTagType.TreasuryKybReadiness, id: companyId },
      ],
    }),
    /** The company KYB — null until it is saved. */
    fetchTreasuryKyb: builder.query<FetchTreasuryKybResponse['data'], number>({
      query: (companyId) => `${TREASURY}/thrift-company-kyb/company/${companyId}`,
      providesTags: (res, error, companyId) => [{ type: ApiTagType.TreasuryKyb, id: companyId }],
      ...FRESH,
    }),
    /** Creates or replaces the company KYB (refused once it has gone to Alpaca). Clears "company verified". */
    saveTreasuryKyb: builder.mutation<
      SaveTreasuryKybResponse['data'],
      CompanyArg & { payload: SaveTreasuryKybRequest }
    >({
      query: ({ companyId, payload }) => ({
        url: `${TREASURY}/thrift-company-kyb/company/${companyId}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (res, error, { companyId }) => [
        { type: ApiTagType.TreasuryKyb, id: companyId },
        { type: ApiTagType.TreasuryKybReadiness, id: companyId },
      ],
    }),
    /** "Company verified": the admin confirms the manual checks (cleared again by a KYB save). */
    verifyTreasuryKyb: builder.mutation<
      VerifyTreasuryKybResponse['data'],
      CompanyArg & { payload: VerifyTreasuryKybRequest }
    >({
      query: ({ companyId, payload }) => ({
        url: `${TREASURY}/thrift-company-kyb/company/${companyId}/verify`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (res, error, { companyId }) => [
        { type: ApiTagType.TreasuryKyb, id: companyId },
        { type: ApiTagType.TreasuryKybReadiness, id: companyId },
      ],
    }),
    /**
     * Accept both Alpaca agreements as this Madkhol admin (the proxy forwards the caller's IP). The first acceptance
     * stays: a company HR admin may already have accepted. Kept on the KYB, and on the setup for an HR acceptance.
     */
    acceptTreasuryAgreements: builder.mutation<AcceptTreasuryAgreementsResponse['data'], number>({
      query: (companyId) => ({
        url: `${TREASURY}/thrift-company-kyb/company/${companyId}/agreements/accept`,
        method: 'PUT',
        body: {},
      }),
      invalidatesTags: (res, error, companyId) => [
        { type: ApiTagType.TreasuryKyb, id: companyId },
        { type: ApiTagType.TreasurySetup, id: companyId },
        { type: ApiTagType.TreasuryKybReadiness, id: companyId },
      ],
    }),
    /** What is still missing before the company Alpaca account can open — `{ ready, missing }`. */
    fetchTreasuryKybReadiness: builder.query<FetchTreasuryKybReadinessResponse['data'], number>({
      query: (companyId) => `${TREASURY}/thrift-company-kyb/company/${companyId}/readiness`,
      providesTags: (res, error, companyId) => [{ type: ApiTagType.TreasuryKybReadiness, id: companyId }],
      ...FRESH,
    }),
    /** The company Alpaca account (if opened) and the newest attempt at opening it, with Alpaca's reason. */
    fetchTreasuryAlpacaOnboarding: builder.query<FetchTreasuryAlpacaOnboardingResponse['data'], number>({
      query: (companyId) => `${TREASURY}/thrift-company-kyb/company/${companyId}/alpaca-onboarding`,
      providesTags: (res, error, companyId) => [{ type: ApiTagType.TreasuryAlpacaOnboarding, id: companyId }],
      ...FRESH,
    }),
    /**
     * "Try again now" — refused while the account is in review / live or ACTION_REQUIRED (409), or while anything is
     * missing (400). The run is async (a queue message to madkhol-alpaca), so the step watches the attempt instead of
     * refetching here.
     */
    retryTreasuryAlpacaOnboarding: builder.mutation<RetryTreasuryAlpacaOnboardingResponse['data'], number>({
      query: (companyId) => ({
        url: `${TREASURY}/thrift-company-kyb/company/${companyId}/alpaca-onboarding/retry`,
        method: 'POST',
        body: {},
      }),
    }),
    /** Screens the owner again — only while readiness lists OWNER_WORLD_CHECK_MISSING. */
    retryTreasuryWorldCheck: builder.mutation<RetryTreasuryWorldCheckResponse['data'], number>({
      query: (companyId) => ({
        url: `${TREASURY}/thrift-company-kyb/company/${companyId}/world-check/retry`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (res, error, companyId) => [
        { type: ApiTagType.TreasuryKybReadiness, id: companyId },
        { type: ApiTagType.TreasuryAlpacaOnboarding, id: companyId },
      ],
    }),

    // ---------------------- OPERATIONS (company Alpaca account ACTIVE) ----------------------
    /** Live: the treasury portfolio's value / return (madkhol-portfolio's card calculation) and the treasury bank. */
    fetchTreasuryAnalysis: builder.query<FetchTreasuryAnalysisResponse['data'], number>({
      query: (companyId) => `${TREASURY}/thrift-companies/company/${companyId}/analysis`,
      providesTags: (res, error, companyId) => [{ type: ApiTagType.TreasuryAnalysis, id: companyId }],
      ...FRESH,
    }),
    /**
     * Dividend reinvest on the company's Alpaca account — answers `{ reinvestDividend }`. The setup isn't refetched:
     * that would reload the whole treasury page for one switch; the switch keeps the answer.
     */
    updateTreasuryReinvestDividend: builder.mutation<
      UpdateTreasuryReinvestDividendResponse['data'],
      CompanyArg & { reinvestDividend: boolean }
    >({
      query: ({ companyId, reinvestDividend }) => ({
        url: `${TREASURY}/thrift-companies/company/${companyId}/reinvest-dividend`,
        method: 'PUT',
        body: { reinvestDividend },
      }),
    }),
    fetchTreasuryContributions: builder.query<FetchTreasuryContributionsResponse['data'], ListArg>({
      query: ({ companyId, ...query }) =>
        `${TREASURY}/thrift-company-contributions/company/${companyId}?${toQueryParams(query)}`,
      providesTags: (res, error, { companyId }) => [{ type: ApiTagType.TreasuryContribution, id: companyId }],
      ...FRESH,
    }),
    /** A PENDING contribution of `amount`, 100% company. No monthly limit. */
    createTreasuryContribution: builder.mutation<
      CreateTreasuryContributionResponse['data'],
      CompanyArg & { amount: number }
    >({
      query: ({ companyId, amount }) => ({
        url: `${TREASURY}/thrift-company-contributions/company/${companyId}`,
        method: 'POST',
        body: { amount },
      }),
      invalidatesTags: (res, error, { companyId }) => [{ type: ApiTagType.TreasuryContribution, id: companyId }],
    }),
    /** Debits the company VIRTUAL_TREASURY bank and starts the buy — the bank card reads again too. */
    executeTreasuryContribution: builder.mutation<
      ExecuteTreasuryContributionResponse['data'],
      CompanyArg & { contributionId: number }
    >({
      query: ({ companyId, contributionId }) => ({
        url: `${TREASURY}/thrift-company-contributions/company/${companyId}/${contributionId}/execute`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: (res, error, { companyId }) => [
        { type: ApiTagType.TreasuryContribution, id: companyId },
        { type: ApiTagType.TreasuryAnalysis, id: companyId },
      ],
    }),
    /** PARTIAL or FULL, approved at creation. 409 while another redemption of the portfolio is in flight. */
    createTreasuryRedemption: builder.mutation<
      CreateTreasuryRedemptionResponse['data'],
      CompanyArg & { payload: CreateTreasuryRedemptionRequest }
    >({
      query: ({ companyId, payload }) => ({
        url: `${TREASURY}/thrift-company-redemptions/company/${companyId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (res, error, { companyId }) => [{ type: ApiTagType.TreasuryAnalysis, id: companyId }],
    }),
    fetchTreasuryRedeemAutomations: builder.query<FetchTreasuryRedeemAutomationsResponse['data'], number>({
      query: (companyId) => `${TREASURY}/thrift-company-redeem-automations/company/${companyId}`,
      providesTags: (res, error, companyId) => [{ type: ApiTagType.TreasuryRedeemRule, id: companyId }],
      ...FRESH,
    }),
    createTreasuryRedeemAutomation: builder.mutation<
      SaveTreasuryRedeemAutomationResponse['data'],
      CompanyArg & { payload: SaveTreasuryRedeemAutomationRequest }
    >({
      query: ({ companyId, payload }) => ({
        url: `${TREASURY}/thrift-company-redeem-automations/company/${companyId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (res, error, { companyId }) => [{ type: ApiTagType.TreasuryRedeemRule, id: companyId }],
    }),
    updateTreasuryRedeemAutomation: builder.mutation<
      SaveTreasuryRedeemAutomationResponse['data'],
      RuleArg & { payload: UpdateTreasuryRedeemAutomationRequest }
    >({
      query: ({ companyId, id, payload }) => ({
        url: `${TREASURY}/thrift-company-redeem-automations/company/${companyId}/${id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (res, error, { companyId }) => [{ type: ApiTagType.TreasuryRedeemRule, id: companyId }],
    }),
    pauseTreasuryRedeemAutomation: builder.mutation<SaveTreasuryRedeemAutomationResponse['data'], RuleArg>({
      query: ({ companyId, id }) => ({
        url: `${TREASURY}/thrift-company-redeem-automations/company/${companyId}/${id}/pause`,
        method: 'PUT',
        body: {},
      }),
      invalidatesTags: (res, error, { companyId }) => [{ type: ApiTagType.TreasuryRedeemRule, id: companyId }],
    }),
    resumeTreasuryRedeemAutomation: builder.mutation<SaveTreasuryRedeemAutomationResponse['data'], RuleArg>({
      query: ({ companyId, id }) => ({
        url: `${TREASURY}/thrift-company-redeem-automations/company/${companyId}/${id}/resume`,
        method: 'PUT',
        body: {},
      }),
      invalidatesTags: (res, error, { companyId }) => [{ type: ApiTagType.TreasuryRedeemRule, id: companyId }],
    }),
    deleteTreasuryRedeemAutomation: builder.mutation<DeleteTreasuryRedeemAutomationResponse['data'], RuleArg>({
      query: ({ companyId, id }) => ({
        url: `${TREASURY}/thrift-company-redeem-automations/company/${companyId}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (res, error, { companyId }) => [{ type: ApiTagType.TreasuryRedeemRule, id: companyId }],
    }),
    fetchTreasuryRedeemAutomationRuns: builder.query<FetchTreasuryRedeemAutomationRunsResponse['data'], RuleArg>({
      query: ({ companyId, id }) => `${TREASURY}/thrift-company-redeem-automations/company/${companyId}/${id}/runs`,
      providesTags: (res, error, { id }) => [{ type: ApiTagType.TreasuryRedeemRun, id }],
      ...FRESH,
    }),
    /** The treasury withdraw door — same shapes as ratbi+, held and settled on the VIRTUAL_TREASURY bank. */
    fetchTreasuryWithdrawRequests: builder.query<FetchCompanyWithdrawRequestsResponse['data'], ListArg>({
      query: ({ companyId, ...query }) =>
        `${TREASURY}/thrift-company-withdraw-requests/company/${companyId}?${toQueryParams(query)}`,
      providesTags: (res, error, { companyId }) => [{ type: ApiTagType.TreasuryWithdrawRequest, id: companyId }],
      ...FRESH,
    }),
    /** Holds the amount on the treasury bank — the bank card reads again. */
    createTreasuryWithdrawRequest: builder.mutation<
      CreateCompanyWithdrawRequestResponse['data'],
      CompanyArg & { payload: CreateCompanyWithdrawRequestRequest }
    >({
      query: ({ companyId, payload }) => ({
        url: `${TREASURY}/thrift-company-withdraw-requests/company/${companyId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (res, error, { companyId }) => [
        { type: ApiTagType.TreasuryWithdrawRequest, id: companyId },
        { type: ApiTagType.TreasuryAnalysis, id: companyId },
      ],
    }),
    /** Settles the hold. */
    approveTreasuryWithdrawRequest: builder.mutation<ApproveCompanyWithdrawRequestResponse['data'], RuleArg>({
      query: ({ companyId, id }) => ({
        url: `${TREASURY}/thrift-company-withdraw-requests/company/${companyId}/${id}/approve`,
        method: 'PUT',
        body: {},
      }),
      invalidatesTags: (res, error, { companyId }) => [
        { type: ApiTagType.TreasuryWithdrawRequest, id: companyId },
        { type: ApiTagType.TreasuryAnalysis, id: companyId },
      ],
    }),
    /** Releases the hold back to the treasury bank. */
    rejectTreasuryWithdrawRequest: builder.mutation<RejectCompanyWithdrawRequestResponse['data'], RuleArg>({
      query: ({ companyId, id }) => ({
        url: `${TREASURY}/thrift-company-withdraw-requests/company/${companyId}/${id}/reject`,
        method: 'PUT',
        body: {},
      }),
      invalidatesTags: (res, error, { companyId }) => [
        { type: ApiTagType.TreasuryWithdrawRequest, id: companyId },
        { type: ApiTagType.TreasuryAnalysis, id: companyId },
      ],
    }),
  }),
});

export const {
  useFetchTreasurySetupQuery,
  useApproveCompanyForTreasuryMutation,
  useFetchTreasuryOnboardingQuery,
  useCreateTreasuryEmployeeMutation,
  useUpdateTreasuryPersonalEmailMutation,
  useApproveTreasuryEmployeeMutation,
  useFetchTreasuryKycConfirmationQuery,
  useConfirmTreasuryKycMutation,
  useFetchTreasuryOwnerAgreementQuery,
  useSignTreasuryOwnerAgreementMutation,
  useFetchTreasuryKybQuery,
  useSaveTreasuryKybMutation,
  useVerifyTreasuryKybMutation,
  useAcceptTreasuryAgreementsMutation,
  useFetchTreasuryKybReadinessQuery,
  useFetchTreasuryAlpacaOnboardingQuery,
  useRetryTreasuryAlpacaOnboardingMutation,
  useRetryTreasuryWorldCheckMutation,
  useFetchTreasuryAnalysisQuery,
  useUpdateTreasuryReinvestDividendMutation,
  useFetchTreasuryContributionsQuery,
  useCreateTreasuryContributionMutation,
  useExecuteTreasuryContributionMutation,
  useCreateTreasuryRedemptionMutation,
  useFetchTreasuryRedeemAutomationsQuery,
  useCreateTreasuryRedeemAutomationMutation,
  useUpdateTreasuryRedeemAutomationMutation,
  usePauseTreasuryRedeemAutomationMutation,
  useResumeTreasuryRedeemAutomationMutation,
  useDeleteTreasuryRedeemAutomationMutation,
  useFetchTreasuryRedeemAutomationRunsQuery,
  useFetchTreasuryWithdrawRequestsQuery,
  useCreateTreasuryWithdrawRequestMutation,
  useApproveTreasuryWithdrawRequestMutation,
  useRejectTreasuryWithdrawRequestMutation,
} = treasuryApiSlice;
